// Shared helpers for Partner pay. Money is integer cents and rates are basis points on the
// wire; the page shows dollars and percents. The server validates everything again.
import { AlertTriangle } from 'lucide-react';
import { Money, usd, parseCents, centsToInput, inputCls, btnCls, btn2Cls, cardCls, thCls, tdCls, Empty, fmtDate } from '../books/shared';

export { Money, usd, parseCents, centsToInput, inputCls, btnCls, btn2Cls, cardCls, thCls, tdCls, Empty, fmtDate };

export const KIND_LABEL = { direct_cost: 'Direct costs', payback: 'Payback', overhead: 'Overhead', role_fee: 'Role fee', remainder: 'Remainder share' };
export const DEAL_ROLES = [
  { key: 'sales_lead', label: 'Sales Lead' },
  { key: 'delivery_lead', label: 'Delivery Lead' },
  { key: 'ops_lead', label: 'Operations Lead' },
];

// "30" or "2.5" -> 3000 / 250; null when not a percent with at most 2 decimals.
export function parseBps(v) {
  const m = /^(\d{1,3})(?:\.(\d{1,2}))?$/.exec(String(v ?? '').replace(/[%\s]/g, ''));
  if (!m) return null;
  const n = Number(m[1]) * 100 + Number((m[2] || '0').padEnd(2, '0'));
  return n <= 10000 ? n : null;
}
export const bpsToInput = (b) => (b == null ? '' : String(b / 100));
export const pctText = (b) => `${(b / 100).toFixed(b % 100 ? 2 : 0)}%`;

// A key for a new role or payback (lower_snake_case, 2 to 40 characters, starting with a letter).
const slug = (s) => {
  const k = String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/^([^a-z])/, 'r_$1').slice(0, 40);
  return k.length === 1 ? `${k}_1` : k;
};

/** Terms (wire) -> editable form (strings for the inputs). */
export function toForm(t) {
  return {
    order: t.order || 'fees_first',
    ownership: (t.ownership || []).map((o) => ({ partner: o.partner, pct: bpsToInput(o.bps) })),
    roles: (t.roles || []).map((r) => ({
      key: r.key, label: r.label, paidTo: r.holder === 'deal' ? `deal:${r.key}` : `partner:${r.holder}`,
      tiers: r.tiers.map((x) => ({ upTo: x.up_to_cents == null ? '' : centsToInput(x.up_to_cents), pct: bpsToInput(x.bps) })),
    })),
    paybacks: {
      split: t.paybacks?.split || 'pro_rata',
      items: (t.paybacks?.items || []).map((i) => ({ key: i.key, label: i.label, partner: i.partner || '', amount: centsToInput(i.amount_cents) })),
    },
    overhead: { label: t.overhead?.label || 'Overhead at cost', monthly: centsToInput(t.overhead?.monthly_cents || 0), start_month: t.overhead?.start_month || '' },
    flag_dual_role: t.flag_dual_role !== false,
  };
}

/** Editable form -> terms (wire). Returns { terms, errors }; the server checks the rest. */
export function fromForm(f) {
  const errors = [];
  const ownership = f.ownership.map((o) => {
    const bps = parseBps(o.pct);
    if (bps == null) errors.push(`${o.partner}: ownership must be a percent like 40 or 33.33`);
    return { partner: o.partner, bps };
  });
  const usedKeys = new Set();
  const roles = f.roles.map((r, i) => {
    const label = String(r.label || '').trim();
    if (!label) errors.push(`Role ${i + 1}: a name is required`);
    // A deal-held role's key is the Books deal assignment it reads; a fixed one keeps its key.
    const deal = r.paidTo.startsWith('deal:');
    const key = deal ? r.paidTo.slice(5) : (r.key || slug(label) || `role_${i + 1}`);
    const holder = deal ? 'deal' : r.paidTo.slice(8);
    if (usedKeys.has(key)) errors.push(`Role ${label || i + 1}: another role already uses the same ${deal ? 'deal role' : 'name'}`);
    usedKeys.add(key);
    const tiers = r.tiers.map((x, j) => {
      const bps = parseBps(x.pct);
      if (bps == null) errors.push(`${label || `Role ${i + 1}`}, rate ${j + 1}: a percent like 30 or 2.5`);
      const last = j === r.tiers.length - 1;
      let up = null;
      if (!last) {
        up = parseCents(x.upTo);
        if (up == null || up <= 0) errors.push(`${label || `Role ${i + 1}`}, rate ${j + 1}: "up to" must be a dollar amount`);
      }
      return { up_to_cents: up, bps };
    });
    return { key, label, holder, tiers };
  });
  // A payback keeps its key for life (repayments carry by it); a new one goes without a key
  // and the server assigns a fresh one.
  const items = f.paybacks.items.map((it, i) => {
    const label = String(it.label || '').trim();
    if (!label) errors.push(`Payback ${i + 1}: a name is required`);
    const amount = parseCents(it.amount);
    if (amount == null) errors.push(`${label || `Payback ${i + 1}`}: amount must be dollars like 9862.32`);
    return { ...(it.key ? { key: it.key } : {}), label, partner: it.partner || null, amount_cents: amount };
  });
  const monthly = parseCents(f.overhead.monthly || '0');
  if (monthly == null) errors.push('Overhead: monthly amount must be dollars');
  if (f.overhead.start_month && !/^\d{4}-(0[1-9]|1[0-2])$/.test(f.overhead.start_month)) errors.push('Overhead: start month must be YYYY-MM');
  if (monthly > 0 && !f.overhead.start_month) errors.push('Overhead: choose the month it starts from');
  return {
    errors,
    terms: {
      order: f.order, ownership, roles,
      paybacks: { split: f.paybacks.split, items },
      overhead: { label: String(f.overhead.label || '').trim() || 'Overhead at cost', monthly_cents: monthly ?? 0, start_month: f.overhead.start_month || null },
      flag_dual_role: !!f.flag_dual_role,
    },
  };
}

/** Plain-language summary of a version's terms. */
export function describeTerms(t) {
  const roles = (t.roles || []).map((r) => {
    const who = r.holder === 'deal' ? `whoever the deal assigns as ${DEAL_ROLES.find((d) => d.key === r.key)?.label || r.key}` : r.holder;
    let prev = 0;
    const rate = r.tiers.length === 1 ? pctText(r.tiers[0].bps) : r.tiers.map((x) => {
      const s = x.up_to_cents == null ? `${pctText(x.bps)} above ${usd(prev)}` : `${pctText(x.bps)} up to ${usd(x.up_to_cents)}`;
      prev = x.up_to_cents; return s;
    }).join(', then ');
    return `${r.label}: ${rate} to ${who}`;
  });
  return {
    order: t.order === 'waterfall' ? 'Paybacks and overhead come off the top; role fees are on what is left.' : 'Role fees come first, on net proceeds; paybacks and overhead come out of the Company share.',
    roles,
    ownership: (t.ownership || []).map((o) => `${o.partner} ${pctText(o.bps)}`).join(', '),
    paybacks: (t.paybacks?.items || []).map((i) => `${i.label} ${usd(i.amount_cents)}`),
    overhead: t.overhead?.monthly_cents ? `${t.overhead.label}: ${usd(t.overhead.monthly_cents)} a month${t.overhead.start_month ? ` from ${t.overhead.start_month}` : ''}` : null,
  };
}

/** One computed amount: who gets what, every line, flags. */
export function ResultView({ result, partners }) {
  if (!result) return null;
  const names = partners.map((p) => p.partner);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="Amount" cents={result.amount_cents} />
        <Stat label="Direct costs" cents={result.direct_cost_cents} />
        <Stat label="Net proceeds" cents={result.net_proceeds_cents} />
        <Stat label="Fee base" cents={result.fee_base_cents} />
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {names.map((n) => {
          const b = result.by_partner?.[n] || { fees_cents: 0, remainder_cents: 0, paybacks_cents: 0, take_cents: 0 };
          return (
            <div key={n} className="rounded-lg border border-[var(--border)] p-3">
              <div className="text-sm font-semibold text-[var(--text-primary)]">{n}</div>
              <div className="text-lg font-semibold"><Money cents={b.take_cents} /></div>
              <div className="text-xs text-[var(--text-secondary)]">fees <Money cents={b.fees_cents} /> + remainder <Money cents={b.remainder_cents} />{b.paybacks_cents ? <>; payback repaid <Money cents={b.paybacks_cents} /></> : null}</div>
            </div>
          );
        })}
      </div>
      <Lines lines={result.lines} />
      <Flags result={result} />
    </div>
  );
}

function Stat({ label, cents }) {
  return <div className="rounded-lg bg-gray-50 p-2 dark:bg-slate-800"><div className="text-xs text-[var(--text-secondary)]">{label}</div><div className="text-sm font-semibold"><Money cents={cents} /></div></div>;
}

export function Lines({ lines }) {
  if (!lines?.length) return null;
  const total = lines.reduce((s, l) => s + l.amount_cents, 0);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr><th className={thCls}>Line</th><th className={thCls}>Who</th><th className={`${thCls} text-right`}>Amount</th><th className={thCls}>Basis</th></tr></thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i} className="border-t border-[var(--border)]">
              <td className={tdCls}><span className="text-xs text-[var(--text-secondary)]">{KIND_LABEL[l.kind]}</span><div>{l.label}</div></td>
              <td className={tdCls}>{l.partner || (l.kind === 'direct_cost' || l.kind === 'overhead' ? 'Company' : '')}</td>
              <td className={`${tdCls} text-right`}><Money cents={l.amount_cents} /></td>
              <td className={`${tdCls} text-xs text-[var(--text-secondary)]`}>{l.basis}</td>
            </tr>
          ))}
          <tr className="border-t-2 border-[var(--border)] font-semibold"><td className={tdCls} colSpan={2}>Adds up to</td><td className={`${tdCls} text-right`}><Money cents={total} /></td><td className={tdCls} /></tr>
        </tbody>
      </table>
    </div>
  );
}

export function Flags({ result }) {
  const notes = [
    ...(result.flags || []).map((f) => f.message),
    ...(result.unassigned || []).map((u) => `${u.label}: nobody holds it on this deal, so its ${usd(u.amount_cents)} stays in the ${u.stays_in}.`),
  ];
  const owed = (result.paybacks || []).filter((p) => p.owed_after_cents > 0);
  return (
    <>
      {notes.length > 0 && (
        <ul className="space-y-1 rounded-lg bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          {notes.map((n, i) => <li key={i} className="flex gap-1"><AlertTriangle size={13} className="mt-0.5 shrink-0" /> {n}</li>)}
        </ul>
      )}
      {(owed.length > 0 || result.overhead?.carried_cents > 0) && (
        <p className="text-xs text-[var(--text-secondary)]">
          Still owed afterwards: {owed.map((p) => `${p.label} ${usd(p.owed_after_cents)}`).join('; ')}{owed.length && result.overhead?.carried_cents ? '; ' : ''}
          {result.overhead?.carried_cents > 0 ? `${result.overhead.label} ${usd(result.overhead.carried_cents)} carried` : ''}.
        </p>
      )}
    </>
  );
}
